import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";

type RuleChannel = { id: string; name: string; isPrivate: boolean };

type FileSlackRule = {
  id: string;
  clientId: string;
  clientName: string;
  folderId: string | null;
  folderPath: string | null;
  isPartnerFolder: boolean;
  muted: boolean;
  channels: RuleChannel[];
};

type SlackChannel = RuleChannel & { isMember: boolean };

type ChannelListResponse = {
  channels: SlackChannel[];
  truncated: boolean;
  botTokenConfigured: boolean;
  botTokenStatus: "ok" | "invalid" | "missing_scope" | null;
};

type ClientRow = { id: string; name: string };

type FolderRow = {
  id: string;
  parentId: string | null;
  name: string;
  isPartnerFolder?: boolean;
};

const ALL_FOLDERS = "__all__";

function folderBreadcrumb(
  folder: FolderRow,
  byId: Map<string, FolderRow>,
): string {
  const names: string[] = [];
  const seen = new Set<string>();
  let current: FolderRow | undefined = folder;
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    names.push(current.name);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  return names.reverse().join(" / ");
}

export function FileSlackRulesCard() {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FileSlackRule | null>(null);
  const [clientId, setClientId] = useState("");
  const [folderId, setFolderId] = useState(ALL_FOLDERS);
  const [muted, setMuted] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [channelFilter, setChannelFilter] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);

  const rulesQuery = useQuery<FileSlackRule[]>({
    queryKey: ["/api/billing/settings/file-slack-rules"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const channelsQuery = useQuery<ChannelListResponse>({
    queryKey: ["/api/integrations/slack/channels", refreshKey],
    queryFn: async () => {
      const suffix = refreshKey > 0 ? "?refresh=1" : "";
      const res = await apiRequest(
        "GET",
        `/api/integrations/slack/channels${suffix}`,
      );
      return res.json();
    },
  });

  const clientsQuery = useQuery<ClientRow[]>({
    queryKey: ["/api/billing/clients"],
    queryFn: getQueryFn({ on401: "throw" }),
    enabled: dialogOpen,
  });

  const foldersQuery = useQuery<FolderRow[]>({
    queryKey: ["/api/files/folders", clientId],
    enabled: dialogOpen && Boolean(clientId),
    queryFn: async () => {
      const res = await apiRequest(
        "GET",
        `/api/files/folders?clientId=${encodeURIComponent(clientId)}`,
      );
      return res.json();
    },
  });

  const channelList = channelsQuery.data;
  const botReady =
    Boolean(channelList?.botTokenConfigured) &&
    channelList?.botTokenStatus !== "invalid" &&
    channelList?.botTokenStatus !== "missing_scope";

  const folderOptions = useMemo(() => {
    const folders = foldersQuery.data ?? [];
    const byId = new Map(folders.map((folder) => [folder.id, folder]));
    return [...folders]
      .map((folder) => ({
        ...folder,
        path: folderBreadcrumb(folder, byId),
      }))
      .sort((a, b) => a.path.localeCompare(b.path));
  }, [foldersQuery.data]);

  const listedIds = new Set((channelList?.channels ?? []).map((channel) => channel.id));
  const missingSelected = (editing?.channels ?? []).filter(
    (channel) => selectedIds.includes(channel.id) && !listedIds.has(channel.id),
  );
  const visibleChannels = (channelList?.channels ?? []).filter((channel) =>
    channel.name.toLowerCase().includes(channelFilter.trim().toLowerCase()),
  );

  function openCreate() {
    setEditing(null);
    setClientId("");
    setFolderId(ALL_FOLDERS);
    setMuted(false);
    setSelectedIds([]);
    setChannelFilter("");
    setDialogOpen(true);
  }

  function openEdit(rule: FileSlackRule) {
    setEditing(rule);
    setClientId(rule.clientId);
    setFolderId(rule.folderId ?? ALL_FOLDERS);
    setMuted(rule.muted);
    setSelectedIds(rule.channels.map((channel) => channel.id));
    setChannelFilter("");
    setDialogOpen(true);
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const channelIds = muted ? [] : selectedIds;
      const body = editing
        ? { muted, channelIds }
        : {
            clientId,
            folderId: folderId === ALL_FOLDERS ? null : folderId,
            muted,
            channelIds,
          };
      const res = await apiRequest(
        editing ? "PATCH" : "POST",
        editing
          ? `/api/billing/settings/file-slack-rules/${editing.id}`
          : "/api/billing/settings/file-slack-rules",
        body,
      );
      return res.json();
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["/api/billing/settings/file-slack-rules"],
      });
      setDialogOpen(false);
      toast({ title: editing ? "Routing rule updated" : "Routing rule saved" });
    },
    onError: (error: Error) => {
      toast({
        title: "Could not save routing rule",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest("DELETE", `/api/billing/settings/file-slack-rules/${id}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["/api/billing/settings/file-slack-rules"],
      });
      toast({ title: "Routing rule deleted" });
    },
    onError: (error: Error) => {
      toast({
        title: "Could not delete routing rule",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const saveDisabled =
    !botReady ||
    !clientId ||
    saveMutation.isPending ||
    (!muted && selectedIds.length === 0);

  return (
    <div className="space-y-4 rounded-lg border p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <h3 className="text-sm font-medium">Routing rules</h3>
          <p className="text-sm text-muted-foreground">
            A folder rule wins over its parent folders, then the client rule,
            then the default webhook. A mute does not fall through. Every
            channel on a rule receives the same batch summary.
          </p>
        </div>
        <Button type="button" onClick={openCreate} disabled={!botReady}>
          Add rule
        </Button>
      </div>

      {channelsQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Checking Slack bot…</p>
      ) : channelsQuery.isError ? (
        <p className="text-sm text-destructive">
          {(channelsQuery.error as Error).message}
        </p>
      ) : !channelList?.botTokenConfigured ? (
        <p className="text-sm text-muted-foreground">
          Connect a Slack bot in{" "}
          <Link href="~/admin/integrations" className="underline">
            Integrations
          </Link>{" "}
          before saving routing rules. Uploads with no rule still use the
          default webhook.
        </p>
      ) : channelList.botTokenStatus === "invalid" ? (
        <p className="text-sm text-destructive">
          Slack rejected the bot token. Paste a new one under{" "}
          <Link href="~/admin/integrations" className="underline">
            Integrations
          </Link>
          .
        </p>
      ) : channelList.botTokenStatus === "missing_scope" ? (
        <p className="text-sm text-destructive">
          The Slack app is missing a required scope. Reinstall it from the
          manifest, then paste the token again under{" "}
          <Link href="~/admin/integrations" className="underline">
            Integrations
          </Link>
          .
        </p>
      ) : null}

      {(rulesQuery.data ?? []).length === 0 ? (
        <p className="text-sm text-muted-foreground">No routing rules yet.</p>
      ) : (
        <ul className="divide-y rounded-md border">
          {(rulesQuery.data ?? []).map((rule) => (
            <li
              key={rule.id}
              className="flex flex-wrap items-center justify-between gap-3 p-3"
            >
              <div className="space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{rule.clientName}</span>
                  <span className="text-sm text-muted-foreground">
                    {rule.folderPath ?? "All folders"}
                  </span>
                  {rule.isPartnerFolder ? (
                    <Badge variant="outline">Supplier</Badge>
                  ) : null}
                  {rule.muted ? <Badge variant="secondary">Muted</Badge> : null}
                </div>
                {rule.muted ? null : (
                  <p className="text-sm text-muted-foreground">
                    {rule.channels.map((channel) => `#${channel.name}`).join(", ")}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => openEdit(rule)}
                  disabled={!botReady}
                >
                  Edit
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    if (!window.confirm("Delete this routing rule?")) return;
                    deleteMutation.mutate(rule.id);
                  }}
                >
                  Delete
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[560px]">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit routing rule" : "Add routing rule"}</DialogTitle>
            <DialogDescription>
              Pick a client and, if you want, one folder. The folder name shown
              here follows renames. Private channels appear after you invite
              @HMS in Slack, then refresh.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Client</Label>
              <Select
                value={clientId}
                disabled={Boolean(editing)}
                onValueChange={(value) => {
                  setClientId(value);
                  if (value !== clientId) setFolderId(ALL_FOLDERS);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a client" />
                </SelectTrigger>
                <SelectContent>
                  {(clientsQuery.data ?? []).map((client) => (
                    <SelectItem key={client.id} value={client.id}>
                      {client.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Folder</Label>
              <Select
                value={folderId}
                onValueChange={setFolderId}
                disabled={!clientId || Boolean(editing)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All folders" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ALL_FOLDERS}>All folders</SelectItem>
                  {folderOptions.map((folder) => (
                    <SelectItem key={folder.id} value={folder.id}>
                      {folder.path}
                      {folder.isPartnerFolder ? " (Supplier)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label htmlFor="file-slack-rule-mute">Mute</Label>
                <p className="text-xs text-muted-foreground">
                  Send nothing for this client or folder. A mute does not fall
                  back to the default channel.
                </p>
              </div>
              <Switch
                id="file-slack-rule-mute"
                checked={muted}
                onCheckedChange={setMuted}
              />
            </div>
            {muted ? null : (
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <Label>Channels</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setRefreshKey((value) => value + 1)}
                  >
                    Refresh
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Private channels show up here only after you invite @HMS in
                  Slack, then refresh.
                </p>
                <Input
                  value={channelFilter}
                  onChange={(event) => setChannelFilter(event.target.value)}
                  placeholder="Filter channels"
                />
                {channelList?.truncated ? (
                  <p className="text-xs text-muted-foreground">
                    Showing the first 2000 channels. Refresh if a channel is
                    missing.
                  </p>
                ) : null}
                {missingSelected.length > 0 ? (
                  <ul className="space-y-2 rounded-md border border-dashed p-3">
                    {missingSelected.map((channel) => (
                      <li key={channel.id} className="flex items-start gap-2">
                        <Checkbox
                          checked
                          onCheckedChange={(value) => {
                            if (value) return;
                            setSelectedIds((current) =>
                              current.filter((id) => id !== channel.id),
                            );
                          }}
                          id={`slack-channel-missing-${channel.id}`}
                        />
                        <label
                          htmlFor={`slack-channel-missing-${channel.id}`}
                          className="text-sm"
                        >
                          <span className="font-medium">#{channel.name}</span>
                          <span className="mt-1 block text-xs text-muted-foreground">
                            Not in the channel list. Invite the bot, or the
                            channel was archived or deleted.
                          </span>
                        </label>
                      </li>
                    ))}
                  </ul>
                ) : null}
                <ScrollArea className="h-56 rounded-md border">
                  <ul className="space-y-2 p-3">
                    {visibleChannels.map((channel) => {
                      const checked = selectedIds.includes(channel.id);
                      return (
                        <li key={channel.id} className="flex items-start gap-2">
                          <Checkbox
                            checked={checked}
                            onCheckedChange={(value) => {
                              if (value && selectedIds.length >= 20) {
                                toast({
                                  title: "A rule can include at most 20 channels",
                                  variant: "destructive",
                                });
                                return;
                              }
                              setSelectedIds((current) =>
                                value
                                  ? [...current, channel.id]
                                  : current.filter((id) => id !== channel.id),
                              );
                            }}
                            id={`slack-channel-${channel.id}`}
                          />
                          <label
                            htmlFor={`slack-channel-${channel.id}`}
                            className="text-sm leading-none"
                          >
                            #{channel.name}
                            {channel.isPrivate ? (
                              <span className="ml-2 text-xs text-muted-foreground">
                                Private
                              </span>
                            ) : null}
                            {!channel.isPrivate && !channel.isMember ? (
                              <span className="mt-1 block text-xs text-muted-foreground">
                                Bot is not in this channel. Posts still go
                                through.
                              </span>
                            ) : null}
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </ScrollArea>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={() => saveMutation.mutate()}
              disabled={saveDisabled}
            >
              {saveMutation.isPending ? "Saving..." : "Save rule"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
